/**
 * Cloudflare Turnstile widget wrapper.
 *
 * Renders the invisible/managed Turnstile challenge and returns the
 * resulting token via onVerify. The script is loaded once per page on first
 * mount; subsequent mounts reuse the existing script tag.
 *
 * Docs: https://developers.cloudflare.com/turnstile/get-started/client-side-rendering/
 *
 * If VITE_TURNSTILE_SITE_KEY is not configured, the widget renders nothing
 * and immediately calls onVerify(null) so the form stays usable in dev.
 */

import React, { useEffect, useRef } from 'react';

// Minimal shape of the global turnstile object we actually use.
declare global {
  interface Window {
    turnstile?: {
      render: (
        el: string | HTMLElement,
        opts: {
          sitekey: string;
          action?: string;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'flexible' | 'compact';
          callback?: (token: string) => void;
          'error-callback'?: (code: string) => void;
          'expired-callback'?: () => void;
          'timeout-callback'?: () => void;
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
    __turnstileLoading?: Promise<void>;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstileLoading) return window.__turnstileLoading;

  window.__turnstileLoading = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile script failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile script failed to load'));
    document.head.appendChild(script);
  });

  return window.__turnstileLoading;
}

interface TurnstileProps {
  /** Called with the token when the challenge succeeds, or null if the widget is disabled / expired / errored. */
  onVerify: (token: string | null) => void;
  /** Action name sent to Cloudflare (useful for analytics). Defaults to 'signup'. */
  action?: string;
  /** Light / dark / auto — defaults to auto. */
  theme?: 'light' | 'dark' | 'auto';
  className?: string;
}

const Turnstile: React.FC<TurnstileProps> = ({ onVerify, action = 'signup', theme = 'auto', className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  useEffect(() => {
    // Dev escape hatch: no site key → skip verification entirely.
    if (!siteKey) {
      onVerifyRef.current(null);
      return;
    }

    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          theme,
          callback: (token: string) => onVerifyRef.current(token),
          'error-callback': () => onVerifyRef.current(null),
          'expired-callback': () => onVerifyRef.current(null),
          'timeout-callback': () => onVerifyRef.current(null),
        });
      })
      .catch(() => {
        // Script failed to load — surface as "no token" so the server can
        // reject cleanly (it will, since TURNSTILE_SECRET_KEY is set in prod).
        if (!cancelled) onVerifyRef.current(null);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try { window.turnstile.remove(widgetIdRef.current); } catch { /* ignore */ }
      }
    };
  }, [siteKey, action, theme]);

  // When the widget is disabled (no site key), render nothing — the parent
  // form should still work so local dev isn't blocked on Cloudflare creds.
  if (!siteKey) return null;

  return <div ref={containerRef} className={className} />;
};

export default Turnstile;
